import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { authEvents, ApiError } from '@/api/http'
import { postUserLogin, postUserRequestPasswordReset, postUserResetPassword } from '@/api/generated/user/user'
import { setCookie } from '@/lib/cookies'
import { useUser } from '@/hooks/use-user'
import { resolvePostLoginDestination } from './loginRedirect'

type View = 'login' | 'forgot' | 'reset'

export function LoginModal() {
  const { reload } = useUser()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>('login')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [returnUrl, setReturnUrl] = useState<string | null>(null)

  // Login form
  const [usernameOrEmail, setUsernameOrEmail] = useState('')
  const [password, setPassword] = useState('')

  // Forgot password form
  const [resetEmail, setResetEmail] = useState('')
  // True once the reset email has been sent — swaps the form for a confirmation
  // so the user isn't left staring at a re-submittable "Send Reset Email" form.
  const [forgotSent, setForgotSent] = useState(false)

  // Reset password form
  const [resetToken, setResetToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const clearMessages = useCallback(() => {
    setError(null)
    setSuccess(null)
  }, [])

  const clearForms = useCallback(() => {
    setUsernameOrEmail('')
    setPassword('')
    setResetEmail('')
    setNewPassword('')
    setConfirmPassword('')
    setForgotSent(false)
    // Clear the reset-flow flag too, so a later ordinary login (e.g. via the
    // Header) isn't mistaken for the reset flow and redirected to the dashboard.
    setResetToken('')
  }, [])

  const switchView = useCallback((newView: View) => {
    clearMessages()
    // Reset the "email sent" confirmation so re-entering Forgot shows the form.
    setForgotSent(false)
    setView(newView)
  }, [clearMessages])

  // Listen for open-login-modal DOM event from Header
  useEffect(() => {
    function handleOpenEvent() {
      clearMessages()
      clearForms()
      setView('login')
      setOpen(true)
    }

    document.addEventListener('open-login-modal', handleOpenEvent)
    return () => {
      document.removeEventListener('open-login-modal', handleOpenEvent)
    }
  }, [clearMessages, clearForms])

  // Listen for unauthorized event from API client
  useEffect(() => {
    function handleUnauthorized(e: Event) {
      const detail = (e as CustomEvent<{ returnUrl?: string }>).detail
      if (detail?.returnUrl) {
        setReturnUrl(detail.returnUrl)
      }
      clearMessages()
      clearForms()
      setView('login')
      setOpen(true)
    }

    authEvents.addEventListener('unauthorized', handleUnauthorized)
    return () => {
      authEvents.removeEventListener('unauthorized', handleUnauthorized)
    }
  }, [clearMessages, clearForms])

  // On mount: check URL for passwordResetToken query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('passwordResetToken')
    if (token) {
      setResetToken(token)
      setView('reset')
      setOpen(true)
    }
  }, [])

  // On mount: open the modal in `login` view when the app loads on `/login`.
  // This is the path-based entry point — anchor/link from ASP pages (e.g.
  // logon.asp's stub) and users who type /login directly land here. `/login`
  // has no page underneath (no route handler), so `cameFromLoginPath` routes
  // post-login to the user dashboard instead of "stay put".
  const [cameFromLoginPath, setCameFromLoginPath] = useState(false)
  useEffect(() => {
    if (window.location.pathname === '/login' || window.location.pathname.startsWith('/login/')) {
      setCameFromLoginPath(true)
      setView('login')
      setOpen(true)
    }
  }, [])

  async function handleLogin(e: FormEvent) {
    e.preventDefault()
    clearMessages()
    setLoading(true)

    try {
      const response = await postUserLogin({ usernameOrEmail, password })
      if (!response.accessToken || response.userId == null) {
        throw new ApiError(500, { message: 'Login failed. Please try again.' })
      }
      setCookie('AccessToken', response.accessToken, 2592000)
      await reload()

      // Refresh cached page data so it reflects the now-authenticated session
      // without requiring a manual browser refresh. The fetcher attaches the
      // AccessToken cookie (just set above) per request, so invalidated queries
      // refetch as the logged-in user — e.g. a roster page's owner-only
      // controls appear immediately instead of after a hard reload.
      void queryClient.invalidateQueries()

      const destination = resolvePostLoginDestination({
        returnUrl,
        cameFromReset: resetToken !== '',
        cameFromLoginPath,
        userId: response.userId,
      })

      setOpen(false)
      clearForms()
      setCameFromLoginPath(false)

      if (destination.kind === 'external') {
        window.location.href = destination.url
        setReturnUrl(null)
      } else if (destination.kind === 'spa') {
        // Leave the throwaway /user/password/reset or /login placeholder;
        // replace so the back button doesn't return to the dead-end page.
        navigate(destination.to, { replace: true })
      }
      // 'stay' → modal closed, page already refreshed in place above.
    } catch (err) {
      const message = (err as ApiError).message || 'Login failed. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  async function handleForgotPassword(e: FormEvent) {
    e.preventDefault()
    clearMessages()
    setLoading(true)

    try {
      await postUserRequestPasswordReset({ email: resetEmail })
      // Swap the form for a confirmation panel (below) rather than leaving a
      // re-submittable form sitting under a success alert.
      setForgotSent(true)
    } catch (err) {
      const message = (err as ApiError).message || 'Failed to send reset email. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault()
    clearMessages()

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)

    try {
      await postUserResetPassword({ token: resetToken, newPassword })
      setSuccess('Password has been reset successfully.')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => {
        switchView('login')
      }, 2000)
    } catch (err) {
      const message = (err as ApiError).message || 'Failed to reset password. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen)
    if (!isOpen) {
      clearMessages()
      clearForms()
      setReturnUrl(null)
      // If the user opened the modal by landing on /login and then dismissed
      // it without logging in, send them home — /login has no page underneath
      // and we'd otherwise strand them on a blank route.
      if (cameFromLoginPath) {
        setCameFromLoginPath(false)
        window.location.href = '/'
      }
    }
  }

  const title =
    view === 'login' ? 'Log In' :
    view === 'forgot' ? 'Reset Password' :
    'Set New Password'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {view === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-username">Email or Username</Label>
              <Input
                id="login-username"
                type="text"
                value={usernameOrEmail}
                onChange={(e) => setUsernameOrEmail(e.target.value)}
                placeholder="Enter your email or username"
                required
                autoComplete="username"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-sm"
                onClick={() => switchView('forgot')}
              >
                Forgot password?
              </Button>
            </div>
          </form>
        )}

        {view === 'forgot' && forgotSent && (
          // Confirmation state — the form is gone so there's no ambiguous
          // re-submittable "Send Reset Email" button under a success message.
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              If an account exists for{' '}
              <span className="font-medium text-foreground">{resetEmail}</span>, a
              password reset link is on its way. Check your inbox — and your spam
              folder, just in case. The link can take a minute to arrive.
            </p>
            <Button type="button" className="w-full" onClick={() => switchView('login')}>
              Back to login
            </Button>
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-sm"
                onClick={() => setForgotSent(false)}
              >
                Use a different email
              </Button>
            </div>
          </div>
        )}

        {view === 'forgot' && !forgotSent && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                autoComplete="email"
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Email'}
            </Button>
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-sm"
                onClick={() => switchView('login')}
              >
                Back to login
              </Button>
            </div>
          </form>
        )}

        {view === 'reset' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                autoComplete="new-password"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                autoComplete="new-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-sm"
                onClick={() => switchView('login')}
              >
                Back to login
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
